/**
 * @file 数组相关的 utils
 */
import { isEqual, differenceWith } from './lodash';
/**
 * 将指定 key 对应的值为 null 或 undefined 的成员从数组里剔除, 并修正返回值的类型.
 * 在返回值的类型中, 该 key 对应的值不再是 optional, 而是一定存在.
 *
 * @category Array
 * @param array - 原数组
 * @param key - 要检查的 key
 * @returns 新数组
 */
export const compactByKey = (array, key) => array.filter(e => e[key] != null);
/**
 * 按指定的字段 prop 将数组 `T[]` 转换为一个形如 `{ T[prop]: T }` 的 map.
 *
 * @category Array
 * @param array - 输入的数组
 * @param prop - 从数组成员中取该字段的值作为 map 的 key
 * @returns 转换后的 map
 *
 * @example
 * ```javascript
 * arrayToMap(
 *   [
 *     { id: 1, name: 'Tom' },
 *     { id: 2, name: 'Jerry' },
 *   ],
 *   'id',
 * ) // returns { 1: { id: 1, name: 'Tom' }, 2: { id: 2, name: 'Jerry' } }
 * ```
 */
export function arrayToMap(array, prop) {
    return array.reduce((map, item) => {
        // eslint-disable-next-line
        map[item[prop]] = item;
        return map;
    }, {});
}
/**
 * 将数组中的指定成员替换为一个新的对象. 不改变原数组, 返回新数组.
 *
 * @category Array
 * @param array - 数组
 * @param member - 要被替换的对象
 * @param newMember - 要替换为的新对象
 * @returns 替换后的新数组
 */
export function replace(array, member, newMember) {
    const newArray = [...array];
    const index = array.findIndex(m => m === member);
    if (index !== -1)
        newArray[index] = newMember;
    return newArray;
}
/**
 * 对比新旧数据（默认以 id 为主键），获取增删改的数据
 *
 * @category Array
 * @param prev - 原始数据
 * @param next - 新数据
 * @param primaryKey - 主键 key
 * @returns 增删改的数据
 */
export function compareRecords(prev, next, primaryKey = 'id') {
    return {
        added: next.filter(item => !item[primaryKey]),
        removed: differenceWith(prev, next, (a, b) => a[primaryKey] === b[primaryKey]),
        updated: differenceWith(next, prev, (a, b) => a[primaryKey] === b[primaryKey] && isEqual(a, b)).filter(item => item[primaryKey]),
    };
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiYXJyYXkuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi9zcmMvZnVuY3Rpb25zL2FycmF5LnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBOztHQUVHO0FBRUgsT0FBTyxFQUFFLE9BQU8sRUFBRSxjQUFjLEVBQUUsTUFBTSxVQUFVLENBQUE7QUFFbEQ7Ozs7Ozs7O0dBUUc7QUFDSCxNQUFNLENBQUMsTUFBTSxZQUFZLEdBQUcsQ0FJMUIsS0FBVSxFQUNWLEdBQU0sRUFDTixFQUFFLENBQ0YsS0FBSyxDQUFDLE1BQU0sQ0FBQyxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsQ0FBQyxHQUFHLENBQUMsSUFBSSxJQUFJLENBQThDLENBQUE7QUFFaEY7Ozs7Ozs7Ozs7Ozs7Ozs7OztHQWtCRztBQUNILE1BQU0sVUFBVSxVQUFVLENBQXVCLEtBQVUsRUFBRSxJQUFPO0lBQ2xFLE9BQU8sS0FBSyxDQUFDLE1BQU0sQ0FBdUIsQ0FBQyxHQUFHLEVBQUUsSUFBSSxFQUFFLEVBQUU7UUFDdEQsMkJBQTJCO1FBQzNCLEdBQUcsQ0FBQyxJQUFJLENBQUMsSUFBSSxDQUFRLENBQUMsR0FBRyxJQUFJLENBQUE7UUFDN0IsT0FBTyxHQUFHLENBQUE7SUFDWixDQUFDLEVBQUUsRUFBRSxDQUFDLENBQUE7QUFDUixDQUFDO0FBRUQ7Ozs7Ozs7O0dBUUc7QUFDSCxNQUFNLFVBQVUsT0FBTyxDQUFJLEtBQVUsRUFBRSxNQUFTLEVBQUUsU0FBWTtJQUM1RCxNQUFNLFFBQVEsR0FBRyxDQUFDLEdBQUcsS0FBSyxDQUFDLENBQUE7SUFDM0IsTUFBTSxLQUFLLEdBQUcsS0FBSyxDQUFDLFNBQVMsQ0FBQyxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsS0FBSyxNQUFNLENBQUMsQ0FBQTtJQUNoRCxJQUFJLEtBQUssS0FBSyxDQUFDLENBQUM7UUFBRSxRQUFRLENBQUMsS0FBSyxDQUFDLEdBQUcsU0FBUyxDQUFBO0lBQzdDLE9BQU8sUUFBUSxDQUFBO0FBQ2pCLENBQUM7QUFPRDs7Ozs7Ozs7R0FRRztBQUNILE1BQU0sVUFBVSxjQUFjLENBQzVCLElBQVMsRUFDVCxJQUFTLEVBQ1QsVUFBVSxHQUFHLElBQUk7SUFFakIsT0FBTztRQUNMLEtBQUssRUFBRSxJQUFJLENBQUMsTUFBTSxDQUFDLElBQUksQ0FBQyxFQUFFLENBQUMsQ0FBQyxJQUFJLENBQUMsVUFBVSxDQUFDLENBQUM7UUFDN0MsT0FBTyxFQUFFLGNBQWMsQ0FDckIsSUFBSSxFQUNKLElBQUksRUFDSixDQUFDLENBQUMsRUFBRSxDQUFDLEVBQUUsRUFBRSxDQUFDLENBQUMsQ0FBQyxVQUFVLENBQUMsS0FBSyxDQUFDLENBQUMsVUFBVSxDQUFDLENBQzFDO1FBQ0QsT0FBTyxFQUFFLGNBQWMsQ0FDckIsSUFBSSxFQUNKLElBQUksRUFDSixDQUFDLENBQUMsRUFBRSxDQUFDLEVBQUUsRUFBRSxDQUFDLENBQUMsQ0FBQyxVQUFVLENBQUMsS0FBSyxDQUFDLENBQUMsVUFBVSxDQUFDLElBQUksT0FBTyxDQUFDLENBQUMsRUFBRSxDQUFDLENBQUMsQ0FDM0QsQ0FBQyxNQUFNLENBQUMsSUFBSSxDQUFDLEVBQUUsQ0FBQyxJQUFJLENBQUMsVUFBVSxDQUFDLENBQUM7S0FDbkMsQ0FBQTtBQUNILENBQUMifQ==